import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RdsButtonModule, RdsIconsModule } from '@rds/angular-components';

import { MulesoftOffering } from '../../../models/mulesoft-offering';
import { APPROVED_STATUSES } from '../../../services/offerings.service';
import { FaroActionDirective } from '../../../directives/faro-action.directive';
import { downloadTextFile } from '../../../util/download-file.util';
import {
  buildOfferingMarkdown,
  offeringMarkdownFileName,
} from '../../../util/offering-markdown.util';

/**
 * AC4. Two conditions, because "published" means two different things here:
 * the caller fetches the `published` version of the record, and the record
 * carries an approval status of its own. `fetchPublishedOffering` does not
 * filter on the latter the way the catalogue list does, so a record that is
 * published but not approved reaches the page — and must not be exportable.
 *
 * Exported because the hero asks the same question to decide whether to render
 * the row that hosts this button at all. Two copies of the rule would drift,
 * and the symptom would be an empty row holding a 20px gap open.
 */
export function canDownloadOffering(
  offering: MulesoftOffering | undefined,
): boolean {
  return !!offering && APPROVED_STATUSES.includes(offering.approvalStatus ?? '');
}

/**
 * Downloads the offering in view as a Markdown factsheet (INEXP-2446).
 *
 * Rendering lives in `offering-markdown.util` and the browser plumbing in
 * `download-file.util`; this only decides whether the action is offered at all.
 *
 * Sits on its own row at the foot of the offering hero, below the tags: the
 * factsheet is what you take away, so it stays out of the row carrying the two
 * things you came to do, while still being reachable without reading the whole
 * page first.
 *
 * The button hides itself rather than rendering disabled when the offering is
 * not one the catalogue publishes. A disabled control invites a tooltip
 * explaining an approval rule the reader cannot act on, and the rest of the
 * page already omits what does not apply.
 */
@Component({
  selector: 'inexp-offering-download',
  imports: [RdsButtonModule, RdsIconsModule, FaroActionDirective],
  templateUrl: './offering-download.component.html',
  styleUrl: './offering-download.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfferingDownloadComponent {
  /**
   * The offering to export. Absent while the page is still fetching, and on the
   * Storyblok-driven render path, which does not carry the Process API record —
   * both cases simply show no button.
   */
  readonly offering = input<MulesoftOffering | undefined>(undefined);

  /**
   * AC4, via the shared rule above. Kept even though the hero already checks
   * it: the component has to stay safe wherever it is dropped, and `download()`
   * reads it before writing a file.
   */
  readonly canDownload = computed(() => canDownloadOffering(this.offering()));

  /**
   * The accessible name, which has to *contain* the visible "Download .md"
   * (WCAG 2.5.3 Label in Name) — otherwise a speech-input user saying "click
   * Download .md" activates nothing. The old name opened with "Download this
   * offering…", which does not contain it.
   *
   * It names the offering rather than saying "this one" because the button now
   * sits in the hero directly after the caption's links to *other* offerings,
   * where "this" has stopped being obvious.
   */
  readonly downloadLabel = computed(() => {
    const name = this.offering()?.displayName;
    return name ? `Download .md factsheet for ${name}` : 'Download .md factsheet';
  });

  download(): void {
    const offering = this.offering();
    if (!offering || !this.canDownload()) {
      return;
    }

    downloadTextFile(
      buildOfferingMarkdown(offering, { sourceUrl: window.location.href }),
      offeringMarkdownFileName(offering),
      'text/markdown;charset=utf-8',
    );
  }
}
